import { ModalOpenButton } from "@msys/ui";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import { Button } from "@mui/material";
import React, { useMemo } from "react";
import { Agreement, PermissionName } from "../../../clients/graphqlTypes.js";
import { RestrictedByDocumentPermissionWithDebug } from "../../auth/RestrictedByDocumentPermission.js";
import { ConfirmModalProps } from "../../commons/modals/ConfirmProcess.js";
import { REQUIREMENT_ITEM_TYPES } from "../../constants.js";
import { useProjectRequirementContext } from "../../main-routes/projects/ProjectRequirementContext.js";
import { isItemVisibleToOtherSide } from "../../trees-virtual/helpers.js";
import { AdditionalItemInput, VirtualItem } from "../../trees-virtual/types.js";
import { BareTreeItem } from "../../trees/BareTreeItem.js";
import { TreeDataContainer } from "../../trees/components/TreeDataContainer.js";
import { TreeRightIcons } from "../../trees/components/TreeRightIcons.js";
import { InputComponentProps, ItemComponentProps } from "../../trees/types.js";
import { assertNever } from "../../utils.js";
import { CreateItemInput } from "../doc-items/CreateItemInput.js";
import { DecisionContingencyCheckbox } from "../doc-items/fields/DecisionContingencyCheckbox.js";
import { DecisionOptionRadioOrCheckbox } from "../doc-items/fields/DecisionOptionRadioOrCheckbox.js";
import { DocItemContextMenuItems } from "../doc-items/menus/DocItemContextMenuItems.js";
import { RequirementItemCompletionWizardModal } from "../requirements/RequirementItemCompletionWizardModal.js";
import {
  RequirementTreeItem_Data123Fragment,
  RequirementTreeItem_TreeItemFragment,
} from "./requirement-trees.generated.js";

export function createTreeItemInput<
  Item extends VirtualItem,
  ChildItem extends VirtualItem,
>({
  requirementId,
  createItem,
  createItemByType,
  createItemFromTemplate,
  createItemsFromTemplates,
  createItemsWithProducts,
  pasteItem,
  isAllowedToPasteItem,
}: {
  requirementId: string;
} & Pick<
  React.ComponentProps<typeof CreateItemInput>,
  | "createItem"
  | "createItemByType"
  | "createItemFromTemplate"
  | "createItemsFromTemplates"
  | "createItemsWithProducts"
  | "pasteItem"
  | "isAllowedToPasteItem"
>) {
  return (itemProps: InputComponentProps<Item, ChildItem>) => (
    <CreateItemInput
      doc={{
        id: requirementId,
        docType: "REQUIREMENT",
        templateIsDraft: false,
      }}
      documentItemTypes={REQUIREMENT_ITEM_TYPES}
      createItem={createItem}
      createItemByType={createItemByType}
      createItemFromTemplate={createItemFromTemplate}
      createItemsFromTemplates={createItemsFromTemplates}
      createItemsWithProducts={createItemsWithProducts}
      pasteItem={pasteItem}
      isAllowedToPasteItem={isAllowedToPasteItem}
      {...itemProps}
    />
  );
}

export const createTreeItem =
  <T extends RequirementTreeItem_TreeItemFragment>({
    requirementId,
    pathToDocPage,
    projectId,
    document,
    docAgreement,
    startConfirmProcess,
    expandedItemIds,
    setItemExpanded,
    pasteItem,
    isAllowedToPasteItem,
    onAdditionalItemInputChange,
  }: {
    pathToDocPage: string;
    requirementId: string;
    projectId: string;
    document: {
      id: string;
      viewerPermissions: PermissionName[];
    };
    docAgreement: Agreement;
    startConfirmProcess: (
      props: ConfirmModalProps
    ) => Promise<boolean | undefined>;
    expandedItemIds: string[] | undefined;
    setItemExpanded: (id: string, expanded: boolean) => void;
    pasteItem: ((docId: string, parentItemId: string) => Promise<void>) | null;
    isAllowedToPasteItem: (parentItem: T) => boolean;
    onAdditionalItemInputChange: ((input: AdditionalItemInput) => void) | null;
  }) =>
  (itemProps: ItemComponentProps<T>) => {
    const { hasItemAnyQuestions, refetchWizardQuestions } =
      useProjectRequirementContext();

    const [Data123Left, Data123Right] = useMemo(
      () => getData123(itemProps.item, itemProps.isPriceHidden),
      [itemProps.item, itemProps.isPriceHidden]
    );

    const IconRightButtons = useMemo(
      () => (
        <>
          {hasItemAnyQuestions(itemProps.item.id) && (
            <ItemWizardButton
              projectId={projectId}
              requirement={document}
              item={itemProps.item}
              refetchAfterApplyItemAction={refetchWizardQuestions}
            />
          )}
          {itemProps.item.decisionIsContingentItem &&
            !itemProps.item.isItemEliminated && (
              <DecisionContingencyCheckbox
                projectId={projectId}
                docId={requirementId}
                itemId={itemProps.item.id}
                item={itemProps.item}
              />
            )}
          {itemProps.parentItem?.type === "decision" &&
            !itemProps.item.decisionOptionElimination &&
            !itemProps.item.isItemEliminated && (
              <DecisionOptionRadioOrCheckbox
                projectId={projectId}
                docId={requirementId}
                item={itemProps.item}
                expandedItemIds={expandedItemIds}
              />
            )}
        </>
      ),
      [
        hasItemAnyQuestions,
        itemProps.item,
        itemProps.parentItem?.type,
        refetchWizardQuestions,
      ]
    );

    const ContextMenu = useMemo(
      () => (
        <DocItemContextMenuItems
          pathToDocPage={pathToDocPage}
          docId={requirementId}
          docType="REQUIREMENT"
          projectId={projectId}
          item={itemProps.item}
          itemParentType={itemProps.parentItem?.type ?? null}
          startConfirmProcess={startConfirmProcess}
          expandedItemIds={expandedItemIds}
          pasteItem={isAllowedToPasteItem(itemProps.item) ? pasteItem : null}
          onAdditionalItemInputChange={onAdditionalItemInputChange}
        />
      ),
      [itemProps.item, itemProps.parentItem?.type]
    );

    const RightIcons = useMemo(
      () => (
        <TreeRightIcons
          document={document}
          item={itemProps.item}
          isVisibleToOtherSide={isItemVisibleToOtherSide(
            itemProps.item,
            itemProps.allItems
          )}
        />
      ),
      [itemProps.item, itemProps.allItems]
    );

    return (
      <BareTreeItem
        docAgreement={docAgreement}
        Data123Left={Data123Left}
        Data123Right={Data123Right}
        IconRightButtons={IconRightButtons}
        ContextMenu={ContextMenu}
        RightIcons={RightIcons}
        showPath={itemProps.parentItem?.type !== "decision"}
        item={itemProps.item}
        isRootItem={itemProps.isRootItem}
        depth={itemProps.depth}
        isGreyedOut={itemProps.isGreyedOut}
        isHidden={itemProps.isHidden}
        isPriceHidden={itemProps.isPriceHidden}
        collapseButton={itemProps.collapseButton}
        subcontractPath={null}
        onClick={() => {
          setItemExpanded(itemProps.item.id, true);
        }}
        to={`${pathToDocPage}/items/${itemProps.item.id}`}
      />
    );
  };

export const getData123 = (
  item: RequirementTreeItem_Data123Fragment,
  isPriceHidden: boolean
): [JSX.Element | null, JSX.Element | null] => {
  if (isPriceHidden) {
    return [null, null];
  }

  switch (item.type) {
    case "paid": {
      // TODO this is causing issues for deleted items; for now just show "null" as value instead of breaking
      const calculateForQuote = item.proposedCalculation;

      if (!calculateForQuote) return [null, null];

      return [
        <TreeDataContainer key="data123-left">
          <div>{calculateForQuote.quantity}x</div>
        </TreeDataContainer>,
        null,
      ];
    }
    case "section": {
      const calculateForQuote = item.proposedCalculation;

      if (!calculateForQuote) return [null, null];

      if (
        calculateForQuote.pricePerUnit === 0 &&
        calculateForQuote.quantity === 1
      ) {
        return [null, null];
      }

      return [
        <TreeDataContainer key="data123-left">
          {calculateForQuote.quantity !== 1 && (
            <div>{calculateForQuote.quantity}x</div>
          )}
        </TreeDataContainer>,
        null,
      ];
    }
    case "decision": {
      const calculateForQuote = item.proposedCalculation;

      if (!calculateForQuote) return [null, null];

      if (
        calculateForQuote.pricePerUnit === 0 &&
        calculateForQuote.quantity === 1
      ) {
        return [null, null];
      }

      return [
        <TreeDataContainer key="data123-left">
          {calculateForQuote.quantity !== 1 && (
            <div>{calculateForQuote.quantity}x</div>
          )}
        </TreeDataContainer>,
        null,
      ];
    }
    case "unpaid":
    case "defect": {
      return [null, null];
    }
    default:
      throw assertNever(item.type);
  }
};

export const ItemWizardButton = ({
  projectId,
  requirement,
  item,
  refetchAfterApplyItemAction,
}: {
  projectId: string;
  requirement: { id: string; viewerPermissions: PermissionName[] };
  item: { id: string };
  refetchAfterApplyItemAction: () => Promise<any>;
}) => {
  return (
    <RestrictedByDocumentPermissionWithDebug
      permission="TAKE_REQUIREMENTS_DECISION"
      document={requirement}
      key={"decision-wizard"}
    >
      <div
        onMouseDown={e => {
          e.stopPropagation();
        }}
        onTouchStart={e => {
          e.stopPropagation();
        }}
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <ModalOpenButton
          Modal={RequirementItemCompletionWizardModal}
          modalProps={{
            projectId: projectId,
            requirementId: requirement.id,
            itemId: item.id,
            refetchAfterApplyItemAction,
          }}
        >
          <Button
            color={"primary"}
            variant="outlined"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
            sx={{ minWidth: "auto", padding: 5 / 8 }}
          >
            <QuestionMarkIcon
              fontSize="small"
              style={{ fontSize: "1.15rem" }}
            />
          </Button>
        </ModalOpenButton>
      </div>
    </RestrictedByDocumentPermissionWithDebug>
  );
};
